import type {
  HowBuiltSectionMeta,
  HowBuiltSource,
} from "./how-i-built-this-content";

const GITHUB_MAIN = "https://github.com/markitics/agenda/blob/main";

function source(label: string, path: string): HowBuiltSource {
  return { label, href: `${GITHUB_MAIN}/${path}` };
}

export type MovingPartStatus = "Built" | "Release gate" | "Next";

export interface MovingPartScreenAction {
  label: string;
  target: string;
  effect: string;
}

export interface MovingPartScreen {
  id: string;
  group: string;
  title: string;
  status: MovingPartStatus;
  platform: string;
  purpose: string;
  eyebrow: string;
  headline: string;
  lines: string[];
  actions: MovingPartScreenAction[];
  source: HowBuiltSource;
}

export interface MovingPartProcess {
  id: string;
  title: string;
  status: MovingPartStatus;
  trigger: string;
  immediate: string[];
  asynchronous: string[];
  failure: string;
  sources: HowBuiltSource[];
}

export const MOVING_PARTS = {
  section: {
    id: "moving-parts",
    nav: "Moving parts",
    kicker: "If starting from scratch",
    title: "Here are all the moving parts.",
    summary:
      "This is the reconstruction package: what three weeks taught us, the durable shipped-versus-next boundary, every canonical customer screen and action, the background work each action triggers, and one copyable brief for rebuilding the whole product without rediscovering its privacy rules.",
    sources: [
      source("Product brief", "docs/GOAL_BRIEF.md"),
      source("iPhone root navigation", "Agenda/Views/RootView.swift"),
      source("Android application", "androidApp/src/main/java/com/markmoriarty/agenda/AgendaApplication.kt"),
      source("Web application", "web-client/src/app.ts"),
      source("Canonical schema", "cloudflare/migrations/0001_initial.sql"),
      source("Cloudflare architecture", "docs/CLOUDFLARE_ARCHITECTURE.md"),
    ],
  } satisfies HowBuiltSectionMeta,
  intro: {
    title: "If I started again, this is the build brief.",
    summary:
      "Three weeks of implementation changed the shape of the app. The useful artifact is not another status page: it is the durable product decisions, the complete canonical screen graph, the side effects behind each action, and a prompt another AI could implement without rediscovering the privacy model.",
    boundary:
      "This atlas describes the customer product. Marketing, Roadmap, How I Built This, trust pages, and the owner-only For Mark workspace remain separate public or operational surfaces and are linked rather than redrawn here.",
  },
  learned: [
    {
      title: "Start with the moment, not the archive",
      detail:
        "The core job is to surface the right prompt when the chosen people are together. Imports, Search, and AI support that moment; they are not the product's center.",
    },
    {
      title: "Audience and destination are different choices",
      detail:
        "A person or group answers who. Conversation Cards versus Library answers how. One saved item may go to either destination or both without turning its private owner record into a shared record.",
    },
    {
      title: "Publish a projection, never the owner's row",
      detail:
        "A Library item is a separate revocable publication with recipient receipts. Private Notes, Summary, keywords, assignments, and decorative card-back photos do not leak through that projection.",
    },
    {
      title: "Write locally, reconcile deliberately",
      detail:
        "The conversation must survive a bad connection. Native clients keep an offline working copy, use stable mutation identities, and accept the server's authorized canonical result without silently discarding local intent.",
    },
    {
      title: "Stable identity beats clever reconstruction",
      detail:
        "Principals, cards, audiences, assignments, publications, receipts, sessions, and events keep stable IDs. Clients do not synthesize a recipient publication ID from an owner card or guess which saved item a response belongs to.",
    },
    {
      title: "Photos have two unrelated privacy stories",
      detail:
        "Source media explains a saved item and may sync through protected storage. Card-back photos are decorative, app-private, device-local, metadata-stripped, bounded, and never uploaded by that feature.",
    },
    {
      title: "Authorization precedes search and AI",
      detail:
        "Audience and ownership eligibility are resolved before ranking. Summary and keywords are owner-private signals; Notes are excluded. An edited Summary invalidates its old vector before any replacement is accepted.",
    },
    {
      title: "Queues require revision binding",
      detail:
        "At-least-once delivery is normal. Every job is claimed with a lease, bound to the input fingerprint and model version, and completed with a compare-and-swap so stale AI output cannot overwrite a newer human edit.",
    },
    {
      title: "Signup, deletion, and support are product features",
      detail:
        "A private beta for one known account is not a consumer app. A stranger needs neutral signup, first-run orientation, revocable sessions, clear support, and a deletion journey whose fulfillment closes every linked record safely.",
    },
    {
      title: "Entitlement is server authority, not a local toggle",
      detail:
        "Free allowance, purchase, restore, Web access, and App Review must converge on one principal-owned entitlement contract. Store UI, Stripe state, and an installed build are evidence layers, not independent sources of truth.",
    },
    {
      title: "Accessibility changes the interaction model",
      detail:
        "Face-down privacy, reveal gestures, large text, reduced motion, screen-reader order, pass-around use, and destructive confirmations must be designed into every state—not added after the happy path.",
    },
    {
      title: "Source, deployed, installed, and accepted are separate",
      detail:
        "A green test, merged pull request, Cloudflare deployment, Apple-valid build, TestFlight assignment, phone install, and direct physical acceptance prove different things. The next build should contain substantive reviewed work, never ship merely because a timer fired.",
    },
  ],
  roadmap: [
    {
      name: "Built foundation",
      detail:
        "Conversation Cards, audience selection, face-down reveal and outcomes, History, Manage and New Card, native offline copies, authenticated sync, protected source media, local card-back photos, owner Search fallback, the Library data contract, and the Cloudflare-hosted Web surface exist in current source. Platform and release acceptance remain separate evidence.",
      href: "https://convoful.com/how-i-built-this#stack",
    },
    {
      name: "Important to ship soon",
      detail:
        "Finish the ordinary-stranger first run; complete physical candidate acceptance; close reviewer access, paid entitlement, account-deletion fulfillment, support, and store-submission gates; then verify the exact public release rather than inferring it from source or TestFlight.",
      href: "https://convoful.com/roadmap",
    },
    {
      name: "Deliberately later",
      detail:
        "Production semantic ranking, automatic enrichment, richer multi-audience capture, full Android release, broader source ingestion, and growth experiments follow the trustworthy conversation, onboarding, privacy, and release loops.",
      href: "https://convoful.com/roadmap",
    },
  ],
  screenGroups: [
    {
      id: "entry",
      title: "Entry, identity, and first run",
      description: "A stranger can understand the promise, create or recover an account, and reach a useful first state without Mark-specific setup.",
    },
    {
      id: "conversation",
      title: "Conversation Cards",
      description: "Choose who is together, preserve face-down privacy, reveal one prompt, record an outcome, and recover the session later.",
    },
    {
      id: "capture",
      title: "Capture, Manage, and Search",
      description: "Save from inside or outside the app, choose audience and destination explicitly, edit owner-private metadata, and find the exact saved item again.",
    },
    {
      id: "library",
      title: "Library",
      description: "Recipients see a deliberately published Read, Listen, or Watch projection—not the owner's private saved record.",
    },
    {
      id: "account",
      title: "Plan, sync, and account controls",
      description: "Explain local versus cloud work, restore access, revoke sessions, and begin deletion without hiding consequential actions.",
    },
  ],
  screens: [
    {
      id: "welcome",
      group: "entry",
      title: "Welcome",
      status: "Built",
      platform: "iPhone, Android, Web",
      purpose: "Explain Cards versus Library and offer a demo or private entry without exposing content.",
      eyebrow: "Convoful",
      headline: "Save it for the right moment.",
      lines: ["Conversation Cards for together-time", "Library for read, listen, and watch"],
      actions: [
        { label: "Get started", target: "magic-link", effect: "Opens the neutral log-in-or-sign-up request." },
        { label: "See a safe demo", target: "deck", effect: "Opens a synthetic face-down deck with no private account data." },
      ],
      source: source("Web entry surface", "web-client/src/marketing.ts"),
    },
    {
      id: "magic-link",
      group: "entry",
      title: "Email entry",
      status: "Release gate",
      platform: "iPhone and Web",
      purpose: "Use one generic request for sign-in and signup so account existence is not disclosed.",
      eyebrow: "Private account",
      headline: "Enter your email",
      lines: ["Same response for known and eligible new addresses", "Clear removes the locally entered address"],
      actions: [
        { label: "Email me a link", target: "check-inbox", effect: "Rate-limits, creates a hashed single-use challenge, and sends the first-party link." },
        { label: "Back", target: "welcome", effect: "Returns without creating an account or session." },
      ],
      source: source("Magic-link account contract", "cloudflare/migrations/0015_self_service_magic_link_signup.sql"),
    },
    {
      id: "check-inbox",
      group: "entry",
      title: "Check your inbox",
      status: "Release gate",
      platform: "iPhone and Web",
      purpose: "Explain expiry, supersession, and the fact that opening the page alone does not consume the link.",
      eyebrow: "Link sent",
      headline: "Check your inbox",
      lines: ["Single use", "Opening the completion page does not consume it", "Exchange creates or restores the account atomically"],
      actions: [
        { label: "Complete sign-in", target: "onboarding", effect: "Exchanges the fragment token, creates a revocable session, and routes first-time people through onboarding." },
        { label: "Use another email", target: "magic-link", effect: "Supersedes the earlier unconsumed challenge after a new request." },
      ],
      source: source("Account auth implementation", "cloudflare/src/account-auth.ts"),
    },
    {
      id: "onboarding",
      group: "entry",
      title: "First-run introduction",
      status: "Built",
      platform: "iPhone",
      purpose: "Teach the privacy boundary and the two product rhythms before private content appears.",
      eyebrow: "Three short pages",
      headline: "Cards together. Library apart.",
      lines: ["Audience first", "Face down until reveal", "Private Notes stay yours"],
      actions: [
        { label: "Continue", target: "deck", effect: "Advances locally; the final action persists the onboarding version." },
        { label: "Skip intro", target: "deck", effect: "Moves to the last page first; completion is still explicit." },
      ],
      source: source("iPhone onboarding", "Agenda/Views/AgendaFirstLaunchOnboardingView.swift"),
    },
    {
      id: "deck",
      group: "conversation",
      title: "Deck and audience",
      status: "Built",
      platform: "iPhone, Android, Web",
      purpose: "Choose the people currently together and show only eligible face-down cards.",
      eyebrow: "Maya · 12 cards",
      headline: "What should we talk about?",
      lines: ["Prompt text is absent while face down", "Shuffle and filter never widen audience eligibility"],
      actions: [
        { label: "Reveal", target: "revealed-card", effect: "Starts or resumes the audience session and exposes exactly one prompt." },
        { label: "Filter", target: "deck-controls", effect: "Opens source, status, and reset controls without changing ownership." },
      ],
      source: source("iPhone deck", "Agenda/Views/DeckView.swift"),
    },
    {
      id: "audience-create",
      group: "conversation",
      title: "New audience",
      status: "Built",
      platform: "iPhone",
      purpose: "Create a stable owner-scoped person or group without silently switching the people attached to an active conversation.",
      eyebrow: "New audience",
      headline: "Who is this for?",
      lines: ["Audience name", "Private to the owner workspace", "Pending and error states keep the current audience selected"],
      actions: [
        { label: "Create", target: "search", effect: "Validates and creates one stable audience, selects it only after acceptance and any active-session decision, then dismisses to Search." },
        { label: "Cancel", target: "search", effect: "Dismisses back to Search without creating an audience or changing the current selection." },
      ],
      source: source("iPhone audience creation", "Agenda/Views/Search/AgendaSearchView.swift"),
    },
    {
      id: "revealed-card",
      group: "conversation",
      title: "Revealed card",
      status: "Built",
      platform: "iPhone, Android, Web",
      purpose: "Show the prompt only after deliberate reveal, with source context available as a separate action.",
      eyebrow: "Conversation Card",
      headline: "What would we try if we knew we could not fail?",
      lines: ["Audience-scoped assignment", "Source and owner Notes remain distinct", "Upward reveal gesture has an accessible button equivalent"],
      actions: [
        { label: "Open source", target: "source-reader", effect: "Opens protected retained text or media without changing the conversation outcome." },
        { label: "Record outcome", target: "outcome", effect: "Appends one idempotent session event." },
      ],
      source: source("Card presentation", "Agenda/Views/AgendaCardView.swift"),
    },
    {
      id: "outcome",
      group: "conversation",
      title: "Outcome and undo",
      status: "Built",
      platform: "iPhone and Android",
      purpose: "Record discussed, later, or skip as append-only session history with a bounded correction path.",
      eyebrow: "What happened?",
      headline: "Keep the conversation honest.",
      lines: ["Discussed", "Save for later", "Skip", "Undo is a correction event, not row deletion"],
      actions: [
        { label: "Next card", target: "deck", effect: "Applies the chosen event locally, queues sync, and advances the eligible deck." },
        { label: "End conversation", target: "history", effect: "Finalizes the session and opens its prompt-safe recap." },
      ],
      source: source("Conversation session schema", "cloudflare/migrations/0009_conversation_sessions.sql"),
    },
    {
      id: "history",
      group: "conversation",
      title: "History and recap",
      status: "Built",
      platform: "iPhone and Android",
      purpose: "Show when, with whom, and how many cards were handled without exposing prompts by default.",
      eyebrow: "Conversation history",
      headline: "Sunday with Maya",
      lines: ["8 discussed · 2 later · 1 skipped", "Prompt-safe collapsed rows", "Details re-check current authority"],
      actions: [
        { label: "View details", target: "revealed-card", effect: "Shows allowed card detail only after current assignment authority is revalidated." },
        { label: "Start another", target: "deck", effect: "Returns to the active audience deck." },
      ],
      source: source("iPhone History", "Agenda/Views/HistoryView.swift"),
    },
    {
      id: "deck-controls",
      group: "conversation",
      title: "Deck controls and card backs",
      status: "Built",
      platform: "iPhone and Android",
      purpose: "Filter or reset presentation state and curate bounded decorative photos without uploading them.",
      eyebrow: "Deck controls",
      headline: "Make the deck feel like yours.",
      lines: ["Filters stay inside the selected audience", "Card-back photos are device-local", "Removing a photo does not alter a saved item"],
      actions: [
        { label: "Done", target: "deck", effect: "Applies presentation choices locally." },
        { label: "Reset deck view", target: "deck", effect: "Clears local presentation state, not canonical cards or history." },
      ],
      source: source("Deck filters", "Agenda/Views/FilterSheet.swift"),
    },
    {
      id: "share-intake",
      group: "capture",
      title: "Share intake",
      status: "Built",
      platform: "iPhone and Android",
      purpose: "Accept a link, text, or bounded media from another app, checkpoint private intent, and require audience/destination choices.",
      eyebrow: "From the Share sheet",
      headline: "Save for later",
      lines: ["Source identity is retained", "An interrupted draft is recoverable", "Nothing is shared merely because it entered the extension"],
      actions: [
        { label: "Choose audience", target: "new-card", effect: "Moves the bounded draft into the same explicit save contract as in-app capture." },
        { label: "Cancel", target: "manage", effect: "Closes safely; only an intentionally checkpointed private draft may remain." },
      ],
      source: source("iPhone Share extension", "AgendaShareExtension/ShareViewController.swift"),
    },
    {
      id: "new-card",
      group: "capture",
      title: "New Card composer",
      status: "Built",
      platform: "iPhone, Android, Web",
      purpose: "Create a text or link saved item and explicitly choose Cards, Library, or both for one or more audiences.",
      eyebrow: "Private draft",
      headline: "New Card",
      lines: ["Text or link", "Owner-private Notes", "Audience", "Conversation Cards / Library destinations"],
      actions: [
        { label: "Save", target: "manage", effect: "Writes locally first, applies the free-plan gate, then retries the principal-owned save online." },
        { label: "View card", target: "revealed-card", effect: "Uses the exact returned card identity rather than searching by text or time." },
      ],
      source: source("iPhone New Card", "Agenda/Views/AgendaManageRootView.swift"),
    },
    {
      id: "manage",
      group: "capture",
      title: "Manage",
      status: "Built",
      platform: "iPhone and Web",
      purpose: "Browse owner records, inspect destination assignments, edit private fields, and create another saved item offline.",
      eyebrow: "Your saved items",
      headline: "Manage",
      lines: ["Owner workspace only", "Audience and destination badges", "Preparing and locked states fail closed"],
      actions: [
        { label: "Edit item", target: "card-editor", effect: "Opens the authoritative card, assignment, publication, and private metadata editor." },
        { label: "Search", target: "search", effect: "Moves to audience-scoped owner Search." },
      ],
      source: source("iPhone Manage", "Agenda/Views/AgendaManageRootView.swift"),
    },
    {
      id: "card-editor",
      group: "capture",
      title: "Card editor",
      status: "Built",
      platform: "iPhone and Web",
      purpose: "Edit owner-private Notes, Summary, keywords, audience assignments, and publication choices without changing immutable identity.",
      eyebrow: "Saved item",
      headline: "Edit details",
      lines: ["Private Notes never publish", "Summary edit invalidates the old vector", "Removing Library revokes the recipient projection"],
      actions: [
        { label: "Save changes", target: "manage", effect: "Uses optimistic concurrency and an idempotency key; conflicts preserve the draft." },
        { label: "Read source", target: "source-reader", effect: "Requests only authorized retained media for this owner card." },
      ],
      source: source("Search metadata editor", "Agenda/Views/Search/SearchMetadataEditorView.swift"),
    },
    {
      id: "source-reader",
      group: "capture",
      title: "Source reader",
      status: "Release gate",
      platform: "iPhone",
      purpose: "Read retained source text and swipe through ordered protected image/video assets, including offline cache states.",
      eyebrow: "Saved source",
      headline: "The original material",
      lines: ["Ordered assets", "Integrity and owner binding", "Download, cached, unavailable, retry, and canceled states"],
      actions: [
        { label: "Done", target: "card-editor", effect: "Returns without mutating private metadata or conversation state." },
        { label: "Try media again", target: "source-reader", effect: "Retries only the authorized asset request and preserves known-good cached bytes." },
      ],
      source: source("iPhone source reader", "Agenda/Views/AgendaSourceReaderView.swift"),
    },
    {
      id: "search",
      group: "capture",
      title: "Private Search",
      status: "Built",
      platform: "iPhone, Android, Web",
      purpose: "Filter by selected audience and destination before ranking authorized owner results.",
      eyebrow: "Private Search",
      headline: "What do you want to talk about?",
      lines: ["Current production fallback: keyword + Summary", "Notes excluded", "Semantic contribution is zero while a Summary vector is pending"],
      actions: [
        { label: "Open result", target: "card-editor", effect: "Opens the exact authorized saved item." },
        { label: "Read source", target: "source-reader", effect: "Opens its retained source without exposing another audience's item." },
        { label: "New audience", target: "audience-create", effect: "Opens owner-scoped audience creation from the audience menu and fences any active conversation before switching people." },
      ],
      source: source("iPhone Search", "Agenda/Views/Search/AgendaSearchView.swift"),
    },
    {
      id: "library",
      group: "library",
      title: "Library feed",
      status: "Release gate",
      platform: "iPhone, Android, Web",
      purpose: "List live publications for the current recipient as Read, Listen, and Watch material with independent progress.",
      eyebrow: "Library",
      headline: "Shared for your own time",
      lines: ["Publication ID is the authority", "Revoked or ineligible items disappear", "Unread, in progress, and finished are recipient receipts"],
      actions: [
        { label: "Open item", target: "library-detail", effect: "Loads the recipient-safe projection and that principal's receipt." },
        { label: "Conversation Cards", target: "deck", effect: "Changes product mode; it does not convert the publication into an owner card." },
      ],
      source: source("Recipient Library contract", "cloudflare/migrations/0006_recipient_library_receipts.sql"),
    },
    {
      id: "library-detail",
      group: "library",
      title: "Library item",
      status: "Release gate",
      platform: "iPhone, Android, Web",
      purpose: "Show the shared title, source, format, lane, Shared Notes, media, and recipient progress—nothing owner-private.",
      eyebrow: "Watch · From Maya",
      headline: "A deliberately shared item",
      lines: ["Shared Notes", "Protected source media", "Owner Notes, Summary, keywords, and assignments absent"],
      actions: [
        { label: "Mark finished", target: "library", effect: "Updates only this principal's revisioned receipt with replay protection." },
        { label: "Back to Library", target: "library", effect: "Returns to the still-authorized feed." },
      ],
      source: source("Android Library UI", "androidApp/src/main/java/com/markmoriarty/agenda/AgendaLibraryUi.kt"),
    },
    {
      id: "sync",
      group: "account",
      title: "Private sync",
      status: "Built",
      platform: "iPhone, Android, Web",
      purpose: "Explain local work, cloud cursor, pending mutations, identity, and active sessions without exposing credentials.",
      eyebrow: "Private sync",
      headline: "Your devices, one account",
      lines: ["Local changes", "Last accepted cloud cursor", "Active sessions show labels and dates only"],
      actions: [
        { label: "Refresh", target: "sync", effect: "Pulls authorized changes after the cursor and retries pending mutations." },
        { label: "Plan & account", target: "plan-account", effect: "Opens allowance, access, session, and deletion controls." },
      ],
      source: source("iPhone Sync", "Agenda/Views/SyncView.swift"),
    },
    {
      id: "plan-account",
      group: "account",
      title: "Plan and account",
      status: "Release gate",
      platform: "iPhone and Web",
      purpose: "Show the permanent free allowance, current entitlement, restore/refresh controls, and consequential account actions.",
      eyebrow: "Plan & account",
      headline: "Free through 10 Conversation Cards",
      lines: ["Warning begins before the limit", "Existing cards remain usable", "Purchase and restore reconcile to server authority"],
      actions: [
        { label: "Restore access", target: "plan-account", effect: "Refreshes store evidence and reconciles the principal's entitlement idempotently." },
        { label: "Account controls", target: "account-controls", effect: "Shows revocable sessions and the deletion request boundary." },
      ],
      source: source("Paid access UI", "Agenda/Views/AgendaPaidAccessView.swift"),
    },
    {
      id: "account-controls",
      group: "account",
      title: "Sessions and deletion",
      status: "Release gate",
      platform: "iPhone and Web",
      purpose: "Revoke this device or every device and request deletion with explicit confirmation and durable status.",
      eyebrow: "Account controls",
      headline: "Signed-in devices",
      lines: ["This device", "Other session labels and dates", "No credential hashes", "Deletion request revokes every session"],
      actions: [
        { label: "Sign out this device", target: "magic-link", effect: "Revokes only the current session before clearing local authority." },
        { label: "Request deletion", target: "deletion-pending", effect: "Records one pending request, revokes all sessions, and begins the separately verified fulfillment lane." },
      ],
      source: source("Account controls", "Agenda/Views/SyncView.swift"),
    },
    {
      id: "deletion-pending",
      group: "account",
      title: "Deletion requested",
      status: "Release gate",
      platform: "iPhone and Web",
      purpose: "Confirm the request without pretending the destructive relational cleanup has already completed.",
      eyebrow: "Request received",
      headline: "Deletion requested",
      lines: ["Every session is revoked", "Fulfillment and completion delivery are durable separate states", "Unrelated principals must remain byte-identical"],
      actions: [
        { label: "Done", target: "magic-link", effect: "Returns to signed-out entry; it does not cancel or mark fulfillment complete." },
      ],
      source: source("Deletion release operations", "cloudflare/migrations/0019_account_deletion_release_operations.sql"),
    },
  ] satisfies MovingPartScreen[],
  transientStates: [
    "loading or preparing without stale private content",
    "empty with a concrete next action",
    "offline with last-known-good local data",
    "saving or syncing with controls fenced against duplicates",
    "conflict with the person's draft preserved",
    "revoked assignment, publication, session, or account authority",
    "rate limited or temporarily unavailable without account enumeration",
    "destructive confirmation, requested, fulfilled, and completion-delivered as separate states",
    "large text, reduced motion, screen reader, keyboard, and forced-colors equivalents",
  ],
  processes: [
    {
      id: "capture-save",
      title: "Save or edit a card",
      status: "Built",
      trigger: "The person taps Save in New Card, Share intake, or the owner editor.",
      immediate: [
        "Validate the bounded draft, selected audience, and Cards/Library destinations.",
        "Checkpoint the native working copy atomically and return the exact card identity.",
        "Create a stable mutation or owner-workspace idempotency key and preserve the draft until acceptance.",
      ],
      asynchronous: [
        "Retry authenticated sync when online.",
        "The Worker maps the session to a principal, applies optimistic-concurrency and scope checks, writes the canonical card plus assignments/publications in one guarded operation, and appends the authorized change.",
        "Other devices later pull changes after their cursor. Generic card upsert does not automatically enqueue AI while the default-off flag remains false.",
      ],
      failure: "Keep the local intent, show a specific retry/conflict state, and never silently widen sharing or overwrite a newer canonical revision.",
      sources: [
        source("iPhone store", "Agenda/Store/AgendaStore.swift"),
        source("Owner workspace mutation schema", "cloudflare/migrations/0005_owner_workspace_mutations.sql"),
      ],
    },
    {
      id: "native-sync",
      title: "Native sync loop",
      status: "Built",
      trigger: "Launch, foreground, manual refresh, connectivity recovery, or a pending local mutation.",
      immediate: [
        "Load the local cursor and ordered pending mutations.",
        "Send the stable device credential, mutation ID, entity revision, and payload hash.",
      ],
      asynchronous: [
        "The Worker rejects revoked, wrong-principal, or out-of-scope mutations before data access.",
        "An accepted mutation is recorded once and appends a monotonically ordered sync change.",
        "The client pulls authorized changes after its cursor, applies them atomically, then advances the cursor.",
      ],
      failure: "An exact retry returns duplicate without appending again. Native sync receipts do not promise replay of the original response body; ambiguous results are reconciled by pulling canonical state.",
      sources: [
        source("Native sync receipts", "cloudflare/migrations/0002_device_sync.sql"),
        source("iPhone sync", "Agenda/Sync/AgendaSync.swift"),
      ],
    },
    {
      id: "library-publish",
      title: "Publish to Library",
      status: "Release gate",
      trigger: "The owner enables Library for one audience assignment and supplies only shareable fields.",
      immediate: [
        "Keep the owner card and assignment identities unchanged.",
        "Create or update a separate stable publication containing shared title, source, Shared Notes, format, and lane.",
      ],
      asynchronous: [
        "The same guarded write appends the owner change.",
        "Database triggers provision revisioned receipts for currently eligible members; later accepted/restored membership provisions missing receipts.",
        "Recipient reads join membership, live assignment, and live publication before projecting fields. Revocation removes eligibility without deleting the owner card.",
      ],
      failure: "Fail closed: no partial recipient projection, no client-invented publication ID, and no fallback that returns owner Notes, Summary, keywords, or assignments.",
      sources: [
        source("Audience and publication schema", "cloudflare/migrations/0004_audiences_authorization.sql"),
        source("Recipient receipts", "cloudflare/migrations/0006_recipient_library_receipts.sql"),
      ],
    },
    {
      id: "conversation-event",
      title: "Reveal, outcome, undo, and end session",
      status: "Built",
      trigger: "The person reveals a card, records an outcome, undoes it, or ends the conversation.",
      immediate: [
        "Re-check selected audience and active-session identity.",
        "Update local presentation immediately so pass-around use remains responsive.",
      ],
      asynchronous: [
        "Send an idempotent session command.",
        "The Worker appends one immutable event revision and updates the session-card projection in the same guarded operation.",
        "Undo appends a correction referencing the earlier event; ending makes the session final and exposes prompt-safe history.",
      ],
      failure: "Do not double-count retries or mutate an ended session. Preserve local pending state until the canonical event sequence is reconciled.",
      sources: [source("Conversation session schema", "cloudflare/migrations/0009_conversation_sessions.sql")],
    },
    {
      id: "source-media",
      title: "Protected source media",
      status: "Release gate",
      trigger: "An authorized owner or recipient opens retained source text, image, or video.",
      immediate: [
        "Resolve principal, card or publication authority, asset position, lifecycle, and integrity metadata before bytes.",
        "Prefer a valid app-private cached copy for offline reading.",
      ],
      asynchronous: [
        "Fetch protected bytes through the authenticated Worker rather than a public bucket URL.",
        "Verify the expected fingerprint, normalize supported images when captured, and atomically replace the cache.",
        "Cancellation stops the request without deleting a known-good cache entry.",
      ],
      failure: "Expose retry, unavailable, canceled, and integrity-mismatch states separately. Never substitute a different owner's asset or a stale temporary file.",
      sources: [
        source("Owner source media schema", "cloudflare/migrations/0014_owner_source_media.sql"),
        source("iPhone source reader", "Agenda/Views/AgendaSourceReaderView.swift"),
      ],
    },
    {
      id: "summary-embedding",
      title: "Private Summary and embedding",
      status: "Next",
      trigger: "The owner explicitly saves a human Summary, or a separately authorized enrichment job is created.",
      immediate: [
        "Commit the human Summary and immediately invalidate any vector bound to the previous normalized fingerprint.",
        "Insert one deterministic embed job in the same guarded batch for direct Summary intent. Generic card sync remains default-off for automatic jobs.",
      ],
      asynchronous: [
        "An at-least-once Queue delivery conditionally claims the job with a processing lease.",
        "Workers AI receives only the bounded allowed input for that job; Vectorize receives vector metadata, not Notes or private evidence.",
        "Completion re-reads the card and uses fingerprint, Summary revision, model version, and compare-and-swap checks before marking the vector current.",
      ],
      failure: "Retry with backoff and bounded attempts. A stale or terminal duplicate acknowledges without overwriting a newer Summary; a provider failure cannot replace human text.",
      sources: [
        source("Enrichment design", "docs/SEARCH_ENRICHMENT.md"),
        source("Enrichment Worker", "cloudflare/src/enrichment.ts"),
      ],
    },
    {
      id: "magic-link-auth",
      title: "Magic-link sign-in or signup",
      status: "Release gate",
      trigger: "A person submits an email, then explicitly exchanges the single-use fragment token.",
      immediate: [
        "Apply address-hash and requester-hash rate limits before identity lookup.",
        "Return the same generic response for known and eligible unknown addresses; store only a token hash and bounded challenge state.",
        "Send the first-party link. A page GET cannot consume it because the token stays in the URL fragment.",
      ],
      asynchronous: [
        "Exchange atomically consumes one active challenge.",
        "For a new person, create the neutral Me principal, verified identity, owner workspace, Me audience and membership, and one revocable session together.",
        "Web and native receive different revocable session credentials through their platform-appropriate protected stores.",
      ],
      failure: "Expired, superseded, replayed, disabled-user, pending-deletion, and signup-race cases fail closed without revealing whether the account existed.",
      sources: [
        source("Magic-link schema", "cloudflare/migrations/0008_magic_link_accounts.sql"),
        source("Self-service signup", "cloudflare/migrations/0015_self_service_magic_link_signup.sql"),
      ],
    },
    {
      id: "paid-access",
      title: "Free allowance, purchase, and restore",
      status: "Release gate",
      trigger: "Creation approaches the free limit, a purchase completes, a restore is requested, or an entitlement refresh runs.",
      immediate: [
        "Count the authoritative principal's Conversation Cards, warn before the limit, and keep existing cards usable.",
        "Gate only creation beyond the free allowance; never manufacture paid access from a local preference or Store UI result.",
      ],
      asynchronous: [
        "Verify store or Stripe lifecycle evidence on the server and bind it to the canonical principal.",
        "Apply webhook or restore events idempotently and refresh cross-device allowance.",
        "Return one entitlement projection to native and Web clients.",
      ],
      failure: "Stay on the safe free state, preserve existing content, and offer refresh/restore. Test objects, live-billing rehearsal, App Review access, and public purchase availability remain separate proofs.",
      sources: [
        source("Paid access UI", "Agenda/Views/AgendaPaidAccessView.swift"),
        source("Stripe paid-access contract", "docs/STRIPE_PAID_ACCESS_BACKEND.md"),
      ],
    },
    {
      id: "account-deletion",
      title: "Account deletion",
      status: "Release gate",
      trigger: "The authenticated person confirms Request account deletion.",
      immediate: [
        "Record one pending request and revoke every active session in the same authority boundary.",
        "Return a requested state; do not claim the destructive fulfillment is already complete.",
      ],
      asynchronous: [
        "Create an immutable operation bound to the principal and a fingerprinted deletion plan.",
        "Delete or redact the principal's complete relational/reference closure while retaining only explicitly permitted operational evidence.",
        "Verify unrelated-principal byte parity, store the completion receipt, then deliver completion through the separately tracked channel exactly once.",
      ],
      failure: "Remain pending or failed with recoverable evidence. Never partially declare success, reuse a stale plan after the graph changes, or delete records belonging only to another principal.",
      sources: [
        source("Deletion request schema", "cloudflare/migrations/0008_magic_link_accounts.sql"),
        source("Deletion release operations", "cloudflare/migrations/0019_account_deletion_release_operations.sql"),
      ],
    },
  ] satisfies MovingPartProcess[],
  sourceLinks: [
    source("Product brief", "docs/GOAL_BRIEF.md"),
    source("iPhone root navigation", "Agenda/Views/RootView.swift"),
    source("Android application", "androidApp/src/main/java/com/markmoriarty/agenda/AgendaApplication.kt"),
    source("Web application", "web-client/src/app.ts"),
    source("Canonical schema", "cloudflare/migrations/0001_initial.sql"),
    source("Cloudflare architecture", "docs/CLOUDFLARE_ARCHITECTURE.md"),
  ],
  oneShotPrompt: `You are building Convoful from scratch. Produce a complete, testable, privacy-first product in one repository. Work locally and in preview first; do not mutate production accounts, providers, devices, payments, DNS, or user data without a separate explicit gate.

PRODUCT OUTCOME
Convoful helps a person save something for the right human moment. A saved item may become a face-down Conversation Card for an audience when they are together, a deliberately shared Library publication for asynchronous Read/Listen/Watch, or both. Optimize the first release for a trustworthy in-person mobile conversation and for a stranger who is not the creator.

NON-NEGOTIABLE PRODUCT MODEL
1. Audience answers who. Destination answers how. Keep them independent.
2. The owner card is private. A Library publication is a separate, revocable recipient projection with its own stable ID and per-recipient receipt.
3. Never expose owner-private Notes, Summary, keywords, assignments, or local decorative card-back photos in recipient/public responses.
4. Source media and decorative card-back photos are different systems. Source media may sync through protected storage; card-back photos stay bounded, app-private, metadata-stripped, device-local, and are never uploaded by that feature.
5. Every principal, device session, card, audience, membership, assignment, publication, receipt, conversation session, event, mutation, media asset, deletion operation, and job has a stable server-authoritative identity. Do not reconstruct identities from text, timestamps, or related IDs.

PLATFORMS AND STACK
- iPhone: Swift 6 + SwiftUI, Share extension, atomic JSON working copy, protected platform credential, app-private media caches.
- Android: Kotlin + Jetpack Compose, Room working copy, platform secure credential storage.
- Web: TypeScript + Vite, accessible DOM/CSS, secure same-origin session cookie.
- Edge: Cloudflare only. A small public Web Worker serves static assets and forwards same-origin API requests through a service binding. It has no direct D1/R2 access.
- Backend: TypeScript Cloudflare Worker, D1 canonical relational state and ordered change log, private R2 source media, Queue + Workers AI + Vectorize only behind explicit default-off rollout gates.
- One repository for clients, Workers, migrations, local fixtures, verification, and release tooling. Do not provision Vercel.

CANONICAL DATA GRAPH
Principal -> revocable device/browser sessions.
Principal -> owner workspace -> private cards.
Principal -> audiences; audiences -> memberships.
Card + audience -> stable assignment with independent Conversation Cards and Library destination flags.
Library destination -> stable publication containing only shared fields.
Publication + recipient principal -> revisioned progress receipt.
Audience -> conversation sessions -> immutable ordered events and card projections; undo is a correction event.
Card -> ordered source-media metadata; protected bytes live in private object storage.
Card -> human Summary/keywords and optional current embedding metadata; Notes never enter ranking.
Every mutation and command stores the minimum idempotency/revision evidence needed for safe retry.

OFFLINE AND SYNC CONTRACT
Native writes valid user intent locally first. Queue pending mutations with stable IDs and payload hashes. The API authenticates the session, resolves the principal, authorizes the exact object/audience, applies optimistic concurrency and idempotency, appends one ordered change, and returns only an allowed projection. Native duplicate retry must not append twice; reconcile ambiguous responses by pulling canonical state after the cursor. Owner-workspace and conversation commands may store exact response JSON for replay. Apply pulled changes atomically before advancing the cursor. Never discard a draft on conflict or principal transition.

REQUIRED CUSTOMER SCREENS
Implement the complete canonical graph: Welcome; generic Email entry; Check your inbox / magic-link completion; three-page first-run onboarding; Deck + audience; New audience; Revealed card; Outcome + bounded Undo; History/recap; Deck filters + local card-back library; Share intake; New Card composer; Manage list; owner Card editor; protected Source reader; private audience-scoped Search; recipient Library feed; Library item/detail; Private sync; Plan & account; Sessions/account controls; Deletion requested. Add loading, empty, offline, saving, conflict, revoked, rate-limited, destructive-confirmation, requested, fulfilled, and completion-delivered variants where relevant.

PRIMARY NAVIGATION
Mobile product tabs are Cards/Deck, Manage, History, Library, and Search, with Private sync and Plan/account reachable without hiding them. If platform constraints require a different tab count, preserve these destinations and state boundaries. Switching audience during an active conversation requires explicit confirmation. Public marketing, Roadmap, How I Built This, trust/support, and owner-only operations are separate surfaces—not customer tabs.

AUDIENCE CREATION
Audience creation is owner-scoped. Trim and validate the name, create one stable audience and its owner membership, and select it only after the write is accepted. If a conversation session is active, require an explicit switch/end decision before changing its audience. Cancel, validation failure, network failure, and stale completion preserve the current selection; late responses cannot overwrite a newer choice.

CAPTURE AND PUBLICATION
Accept bounded text, link, image, or retained source input from in-app New Card and the native Share surface. Checkpoint interrupted private drafts. Require explicit audience and Cards/Library destinations before sharing. Save locally, then sync. A successful save returns the exact card ID. Creating/updating a Library destination must atomically maintain the stable publication and provision missing eligible-recipient receipts. Revocation removes recipient eligibility without deleting the owner card.

CONVERSATION LOOP
Filter eligible assignments for the chosen audience before display. Face-down UI and accessibility nodes must omit prompt/source content. Reveal deliberately through gesture and accessible button. Start/resume one audience session, append idempotent reveal/outcome/correction/end events, and show prompt-safe History by default. Ended sessions are immutable. Offline UI remains usable and reconciles later without double-counting.

SEARCH AND AI
Authorize ownership/audience/destination before scoring. Designed online ranking is 0.50 semantic + 0.25 keyword + 0.25 owner-private human Summary. When semantic service is globally absent, use fixed 0.50 keyword + 0.50 Summary. Notes are always excluded. One pending card gets semantic contribution zero under the unchanged online denominator; do not renormalize per card. A human Summary save immediately invalidates the prior vector and inserts one revision-bound embed job. Queue delivery is at least once: claim with a lease, bind to normalized fingerprint + Summary revision + model version, retry with backoff, and complete with compare-and-swap. Stale output never overwrites newer human work. Automatic generic enrichment remains false until a separately reviewed production rollout.

AUTHENTICATION
Use one generic magic-link request for known and eligible new addresses. Rate-limit before lookup and do not enumerate account existence. Store only token hashes and bounded challenge state. Put the raw token in the URL fragment so a GET cannot consume it. Explicit exchange is single-use and atomic. A new account receives neutral display name Me, verified identity, owner workspace, Me audience/membership, and one revocable session. Web and native use separate revocable session transports in their platform-appropriate protected stores. Web mutations require exact first-party origin. An invalid explicit native authorization attempt never falls back to browser session authority.

PLAN AND ENTITLEMENT
Offer a permanent free allowance through 10 Conversation Cards, warn before the limit, and gate only creation beyond it; existing cards remain usable. Reconcile purchase, restore, webhook, and cross-device access into one principal-owned server entitlement. Store UI, Stripe test/live evidence, App Review credentials, and public purchase availability are separate gates.

ACCOUNT DELETION
Requesting deletion records one pending request and revokes every session. Fulfillment is a separate durable operation: freeze a fingerprinted plan over the complete relational/reference closure, remove or redact only the target principal's data, preserve unrelated-principal byte parity, store a completion receipt, and deliver completion exactly once. Never claim completion from request creation alone.

ACCESSIBILITY, PRIVACY, AND FAILURE DESIGN
Support 320px width, 200% text, keyboard, screen reader, reduced motion, forced colors, and print where applicable. All targets are at least 44px. Provide button equivalents for gestures. Preserve face-down privacy in visible text and accessibility output. Use synthetic fixtures and never log secrets, raw tokens, private card text, Notes, vectors, credentials, or personal media. Every async action needs idle, pending, retryable, terminal, stale/revoked, and cancellation behavior. Fail closed on uncertain identity or authorization.

TESTS AND ACCEPTANCE JOURNEYS
Write deterministic migrations and contract tests first. Cover: new stranger signup to useful Me workspace; returning sign-in and session revocation; offline New Card then reconnect; interrupted Share draft recovery; audience switch during an active session; face-down accessibility privacy; reveal/outcome/undo/end with duplicate retries; owner edit conflict preserving draft; Cards-only, Library-only, and both destinations; recipient projection omitting every private field; publication revocation; source-media cache/integrity/cancel; Summary edit invalidating stale vector; Queue duplicate/lease/stale-CAS behavior; free card 10 allowed and card 11 gated; restore across devices; deletion request versus fulfillment; unrelated-principal parity; 320px/200%/screen-reader/reduced-motion states.

DELIVERY DISCIPLINE
Treat source, merged, deployed, provider-accepted, installed, physically accepted, submitted, approved, and publicly available as different facts. One issue owns one focused branch and file seam. Make recoverable commits, run narrow then affected full tests, have a different reviewer inspect the exact head, and deploy only the accepted tree. A periodic checkpoint is not a reason to ship; no substantive accepted change means no build.

OUTPUT
Return: repository map; architecture decision record; schema and migrations; API/event contracts; all screens and states; iPhone, Android, and Web implementations; local Cloudflare-compatible development harness; synthetic fixtures; deterministic unit/integration/accessibility/end-to-end tests; privacy threat model; release checklist; and a short list of deliberately deferred features. Keep the first releasable slice centered on stranger onboarding, one excellent private conversation loop, honest Library projection, safe sync, plan/account/deletion controls, and direct acceptance evidence.`,
};
